import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { MatDialog, MatDialogRef, MatDialogActions } from '@angular/material/dialog';
import { PlatformApiService, ResultFilter, Results } from '../../services/platform-api.service';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { AccountAssessment } from '../../models/AssessmentModel';
import { Account } from '../../models/AccountModel';
import { GridListComponent } from '../../shared/grid-list.component'
import { Observable } from 'rxjs';
import { BuyNowPromptComponent } from 'src/app/shared/buynow-prompt.component';
import { SessionService } from 'src/app/services/session.service';

@Component({
  selector: 'cw-ci-assessment-list',
  templateUrl: './assessment-list.component.html',
  styleUrls: ['./assessment-list.component.scss']
})
export class AssessmentListComponent extends GridListComponent<AccountAssessment> implements OnInit {

  @Input() account: Account;
  @Input() subAccount: Account;
  @Input() displaySubAccounts = false;

  showLastOnly = false;
  lastCompleted: Set<number> = new Set<number>();

  constructor(private _platformApi: PlatformApiService, private _sessionService: SessionService, public dialog: MatDialog) { super(); }

  showLastOnlyChange(event) {
    this.showLastOnly = event.checked;
    this.loadItems();
  }

  getLastUpdated(element: AccountAssessment): Date {
    return element.status !== 'COMPLETED' ? element.updatedAt : element.completeDate;
  }

  get columnsToDisplay() {
    if (this.displaySubAccounts) {
      return ['client', 'name', 'progress', 'updatedAt', 'actions'];
    } else {
      return ['name', 'progress', 'updatedAt', 'actions'];
    }
  }

  ngOnInit() {
    if (this.displaySubAccounts || this.subAccount || this.showLastOnly) {
      this.sort.active = 'account_name';
    } else {
      this.sort.active = 'updated_at';
      this.sort.direction = 'desc';
    }
    this.loadItems();
  }
  getItems(filter: ResultFilter): Observable<Results> {
    if (this.displaySubAccounts || this.subAccount || this.showLastOnly) {
      const subAccountId = this.subAccount ? this.subAccount.id : undefined;
      filter.searchData = { showLastOnly: this.showLastOnly, showSubAccounts: this.displaySubAccounts };
      return this._platformApi.getAccountAssessments(this.account.id, subAccountId, filter);
    } else {
      return this._platformApi.getAccountAssessments(this.account.id, undefined, filter);
    }
  }
  onAfterLoad(data: any[]) {
    this.SetLastCompletedIds(data);
  }

  newAssessment(selectedAssessment: AccountAssessment) {
    if (this._sessionService.hasFeature('ALLOW_REASSESSMENT')) {
      let keepAnswers = false;
      const dialogRef = this.dialog.open(AskKeepAnswersPromptComponent, {
        panelClass: 'flush-dialog'
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result != undefined) {
          keepAnswers = result;
          const newAccountAssessment = new AccountAssessment({
            accountId: selectedAssessment.accountId,
            assessmentId: selectedAssessment.assessmentId,
            prevId: selectedAssessment.id,
            surveyResultsJson: keepAnswers ? selectedAssessment.surveyResultsJson : null
          });
          this._platformApi.saveAccountAssessment(this.account, newAccountAssessment).subscribe(accountAssessment => {
            selectedAssessment.nextId = accountAssessment.id;
            selectedAssessment.isLatest = false;
            this._platformApi.saveAccountAssessment(this.account, selectedAssessment).subscribe(prevAcctAssessment => {
              this.stateChange.emit({ state: 'EDIT', selectedAssessment: accountAssessment });
            });
          })
        }
      });
    } else {
      let msg = 'You have reached the assessment limit for your trial.  Please upgrade to a paid subscription to provide unlimited assessments to all of your customers.';
      if (this._sessionService.getCurrentAccount().parentId) {
        msg = 'This feature is not currently available. Please contact your administrator for assistance.'
      }

      const dialogRef = this.dialog.open(BuyNowPromptComponent, {
        panelClass: 'flush-dialog',
        data: { reason: 'FEATUREUNAVAILABLE', message: msg }
      });
    }
  }
  // TODO: remove these and use base class
  workAssessment(selectedAssessment:AccountAssessment) {
    this.stateChange.emit({state:'EDIT', selectedAssessment});
  }

  viewAssessmentDashboard(selectedAssessment: AccountAssessment) {
    this.stateChange.emit({ state: 'REPORT', selectedAssessment });
  }
  /*
  * SetLastCompletedIds - Creates a set of assessment ids grouped by accountId, AssessmentId
  *                       containing the last completed assessment
  */
  private SetLastCompletedIds(data: Array<AccountAssessment>) {
    const ids = {};
    data.forEach(assessment => {
      if (!ids[assessment.accountId]) {
        ids[assessment.accountId] = {};
      }
      if (!ids[assessment.accountId][assessment.assessmentId]) {
        ids[assessment.accountId][assessment.assessmentId] = assessment;
      }
      if (!assessment.completeDate) {
        ids[assessment.accountId][assessment.assessmentId] = assessment;
      }
      else if (ids[assessment.accountId][assessment.assessmentId].completeDate) {
        if (ids[assessment.accountId][assessment.assessmentId].completeDate < assessment.completeDate) {
            ids[assessment.accountId][assessment.assessmentId] = assessment;
        }
      }
    });
    Object.keys(ids).forEach(act => {
      Object.keys(ids[act]).forEach(id => {
        this.lastCompleted.add(ids[act][id].id);
      })
    })
  }
}

@Component({
  selector: 'cw-ci-ask-keep-answers-prompt',
  template: `
  <div class="dashboard-tile" id="ask-keep-answers" style="width:400px; height:250px; box-sizing:content-box;">
    <div class="tile-header">
      <div class="tile-header-title">Prompt</div>
    </div>
    <div class="tile-content" style="text-align: center">
      <h3 style="height: inherit;padding-top: 20px;margin-bottom: 9px;box-sizing: border-box;">Do you want to use your existing answers?</h3>
      <div class="tile-footer centered">
        <button class="mat-raised-button mat-primary" (click)="doKeepAnswers(true)">Yes</button>
        <button class="mat-raised-button" (click)="doKeepAnswers(false)">No</button>
        <button class="mat-raised-button" (click)="cancel()">Cancel</button>
      </div>
  </div>
  `
})
export class AskKeepAnswersPromptComponent {

  public keepAnswers = false;

  constructor(public dialogRef: MatDialogRef<AskKeepAnswersPromptComponent>) {
  }

  doKeepAnswers(value: boolean): void {
    this.keepAnswers = value;
    this.dialogRef.close(value);
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
